import {
  directusApi,
  directusBaseUrl,
  getStoredAccessToken,
} from "../config/directus";
import { hasSeatMapRows } from "../seatLayout/seatMap";
import { routeContainsPickupBeforeDrop, parseRouteStopSchedule } from "../utils/routeStopSchedule";

const CACHE_TTL_MS = 60 * 1000;
const LOCAL_CACHE_TTL_MS = 5 * 60 * 1000;
const LOCAL_CACHE_KEY_ALL_BUSES = "all_buses_cache_v8";
const LOCAL_CACHE_KEY_UNIQUE_BOOKINGS = "unique_bookings_by_nic_v1";
/** Bumped when aggregation/filter rules change (invalidates client cache keys). */
const UNIQUE_BOOKINGS_CACHE_TAG = "_agg_v3";

const BUS_FIELDS =
  "*,route.*,seat_structure.id,seat_structure.name,seat_structure.seat_map,seat_structure.removed_seats";
const NIC_FIELD_KEY = /^(nic|nid|national_id|passenger_nic|nic_no|nic_number|id_number)$/i;
const NIC_FIELD_SUFFIX = /_nic$/i;

function pickNicFromBooking(row) {
  if (!row || typeof row !== "object") return "";
  const candidates = [
    row.nic,
    row.NIC,
    row.nic_no,
    row.nic_number,
    row.national_id,
    row.passenger_nic,
    row.id_number,
    row.nid,
    row.NID,
  ];
  for (const c of candidates) {
    if (c == null) continue;
    const s = String(c).trim();
    if (s !== "") return s;
  }
  for (const key of Object.keys(row)) {
    if (!NIC_FIELD_KEY.test(key) && !NIC_FIELD_SUFFIX.test(key)) continue;
    const v = row[key];
    if (v == null || typeof v === "object") continue;
    const s = String(v).trim();
    if (s !== "") return s;
  }
  return "";
}
const memoryCache = {};

const readCache = (key) => {
  const entry = memoryCache[key];
  if (!entry || !Object.prototype.hasOwnProperty.call(entry, "data")) {
    return null;
  }
  if (Date.now() - entry.ts > CACHE_TTL_MS) return null;
  return entry.data;
};

const writeCache = (key, data) => {
  memoryCache[key] = { ts: Date.now(), data };
};

const clearCache = (key) => {
  delete memoryCache[key];
};

const readLocalCache = (key, ttlMs) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || !parsed?.data) return null;
    if (Date.now() - parsed.ts > ttlMs) return null;
    return parsed.data;
  } catch {
    return null;
  }
};

const writeLocalCache = (key, data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      key,
      JSON.stringify({ ts: Date.now(), data })
    );
  } catch {
    // Ignore quota/localStorage errors.
  }
};

const clearLocalCache = (key) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // Ignore localStorage errors.
  }
};

/** Clears in-memory + persisted caches for `getUniqueBookingsByNIC` (all + status-filtered). */
function clearUniqueBookingsByNicCaches() {
  clearCache("uniqueBookingsByNIC");
  clearCache("uniqueBookingsByNIC__booked");
  clearCache("uniqueBookingsByNIC__booked_agg_v2");
  clearCache(`uniqueBookingsByNIC__booked${UNIQUE_BOOKINGS_CACHE_TAG}`);
  clearLocalCache(LOCAL_CACHE_KEY_UNIQUE_BOOKINGS);
  clearLocalCache(`${LOCAL_CACHE_KEY_UNIQUE_BOOKINGS}__booked`);
  clearLocalCache(`${LOCAL_CACHE_KEY_UNIQUE_BOOKINGS}__booked_agg_v2`);
  clearLocalCache(`${LOCAL_CACHE_KEY_UNIQUE_BOOKINGS}__booked${UNIQUE_BOOKINGS_CACHE_TAG}`);
}

function assertConfigured() {
  if (!directusBaseUrl) {
    throw new Error(
      "Directus is not configured. Set REACT_APP_DIRECTUS_URL in your .env file."
    );
  }
}

function parseJsonField(val, fallback) {
  if (val == null) return fallback;
  if (Array.isArray(val)) return val;
  if (typeof val === "object") return val;
  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return fallback;
    try {
      return JSON.parse(t);
    } catch {
      return fallback;
    }
  }
  return fallback;
}

/**
 * Directus JSON fields may return arrays, JSON strings, or wrapped shapes.
 * Spreading a string as [...stops] would list every character — avoid that.
 */
export function normalizeStopsArray(val) {
  if (val == null) return [];
  if (Array.isArray(val)) {
    return val.map((item) => {
      if (item == null) return "";
      if (typeof item === "string" || typeof item === "number") {
        return String(item);
      }
      if (typeof item === "object") {
        if (typeof item.name === "string") return item.name;
        if (typeof item.label === "string") return item.label;
        if (typeof item.title === "string") return item.title;
      }
      return String(item);
    });
  }
  if (typeof val === "string") {
    const t = val.trim();
    if (!t) return [];
    try {
      const p = JSON.parse(t);
      return normalizeStopsArray(p);
    } catch {
      return t
        .split(/[,|]/)
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  if (typeof val === "object" && Array.isArray(val.stops)) {
    return normalizeStopsArray(val.stops);
  }
  return [];
}

export function buildStructureFromSeatRecord(seatStructure) {
  if (!seatStructure || typeof seatStructure !== "object" || Array.isArray(seatStructure)) {
    return { removed_seats: [], seat_map: null };
  }
  const removed = parseJsonField(seatStructure.removed_seats, []);
  const seat_map = parseJsonField(seatStructure.seat_map, null);
  return {
    ...seatStructure,
    removed_seats: removed,
    seat_map,
  };
}

function getSeatStructureId(rawRef, structure) {
  if (rawRef != null && (typeof rawRef === "number" || typeof rawRef === "string")) {
    return rawRef;
  }
  return structure?.id ?? null;
}

function busNeedsSeatStructureHydration(bus) {
  if (hasSeatMapRows(bus?.structure?.seat_map)) return false;
  const id = bus?.seat_structure_id ?? bus?.structure?.id;
  return id != null && id !== "";
}

export function resolveSeatStructureId(item) {
  if (!item || typeof item !== "object") return null;

  const ref = item.seat_structure;
  if (ref != null && (typeof ref === "number" || typeof ref === "string")) {
    return ref;
  }

  if (item.seat_structure_id != null && item.seat_structure_id !== "") {
    return item.seat_structure_id;
  }

  if (item.structure?.id != null) {
    return item.structure.id;
  }

  return null;
}

/** Load seat_map for seat picker when bus payload omits nested seat_map. */
export async function fetchSeatStructureById(id) {
  if (id == null || id === "") return null;
  try {
    assertConfigured();
    const { data } = await directusApi.get("/items/seat_structures", {
      params: {
        filter: JSON.stringify({ id: { _eq: id } }),
        fields: "id,name,seat_map,removed_seats",
        limit: 1,
      },
    });
    const row = data.data?.[0];
    return row ? buildStructureFromSeatRecord(row) : null;
  } catch (error) {
    console.error("fetchSeatStructureById:", error.message);
    return null;
  }
}

async function hydrateSeatStructures(buses) {
  if (!Array.isArray(buses) || !buses.length) return buses;
  if (!buses.some(busNeedsSeatStructureHydration)) return buses;

  const ids = [
    ...new Set(
      buses
        .filter(busNeedsSeatStructureHydration)
        .map((b) => b.seat_structure_id ?? b.structure?.id)
        .filter((id) => id != null && id !== "")
    ),
  ];
  if (!ids.length) return buses;

  try {
    const { data } = await directusApi.get("/items/seat_structures", {
      params: {
        filter: JSON.stringify({ id: { _in: ids } }),
        fields: "id,name,seat_map,removed_seats",
        limit: -1,
      },
    });
    const byId = new Map();
    for (const row of data.data || []) {
      byId.set(row.id, row);
      byId.set(String(row.id), row);
    }

    return buses.map((bus) => {
      if (!busNeedsSeatStructureHydration(bus)) return bus;
      const sid = bus.seat_structure_id ?? bus.structure?.id;
      const record = byId.get(sid) ?? byId.get(String(sid));
      if (!record) return bus;
      const structure = buildStructureFromSeatRecord(record);
      return {
        ...bus,
        structure,
        seat_map: structure.seat_map ?? null,
      };
    });
  } catch (error) {
    console.error("hydrateSeatStructures:", error.message);
    return buses;
  }
}

export function normalizeBus(raw) {
  if (!raw) return null;
  const pk = raw.id;
  const routeSource =
    raw.route &&
    typeof raw.route === "object" &&
    !Array.isArray(raw.route)
      ? raw.route
      : {};
  const route = {
    ...routeSource,
    stops: normalizeStopsArray(routeSource.stops),
    stopsSchedule: routeSource.stops ?? null,
  };
  const seatStructureRef = raw.seat_structure;
  const structure =
    seatStructureRef != null &&
    typeof seatStructureRef === "object" &&
    !Array.isArray(seatStructureRef)
      ? buildStructureFromSeatRecord(seatStructureRef)
      : buildStructureFromSeatRecord({});
  const seat_structure_id = getSeatStructureId(seatStructureRef, structure);
  const counter_seats = parseJsonField(raw.counter_seats, []);
  const {
    seat_structure: _omitSeatStructure,
    route: _omitRoute,
    ...restRaw
  } = raw;

  return {
    ...restRaw,
    id: raw.bus_number,
    directus_id: pk,
    structure,
    seat_map: structure.seat_map ?? null,
    seat_structure_id,
    route,
    routeSet: route,
    counter_seats,
    fee: parseBusFee(raw.fee),
    runs_everyday: raw.runs_everyday !== false,
    available_dates: normalizeAvailableDates(raw.available_dates),
  };
}

function parseBusFee(val) {
  const n = Number.parseFloat(String(val ?? "").trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

/** Use for UI when `route` may come from cache or partial API shapes. */
export function getRouteStopsForDisplay(route) {
  return normalizeStopsArray(route?.stops);
}

/** Raw Directus route.stops JSON — keeps { name, time }. Never use normalized string-only stops. */
export function getRouteStopsSchedule(itemOrRoute) {
  const route =
    itemOrRoute?.route ??
    itemOrRoute?.routeSet ??
    itemOrRoute ??
    {};
  if (route.stopsSchedule != null) return route.stopsSchedule;
  const raw = route.stops;
  if (Array.isArray(raw) && parseRouteStopSchedule(raw).some((s) => s.time)) {
    return raw;
  }
  return null;
}

export function getRouteIdFromItem(item) {
  const route = item?.route ?? item?.routeSet ?? null;
  if (route == null) return null;
  if (typeof route === "string" || typeof route === "number") return route;
  return route.id ?? null;
}

/** Always load latest stops+times from Directus (avoids stale bus cache missing times). */
export async function fetchRouteStopsSchedule(routeRef) {
  assertConfigured();
  const routeId =
    routeRef != null && typeof routeRef === "object" && !Array.isArray(routeRef)
      ? routeRef.id
      : routeRef;
  if (routeId == null || routeId === "") return null;
  const { data } = await directusApi.get(`/items/routes/${routeId}`, {
    params: { fields: "stops" },
  });
  return data?.data?.stops ?? null;
}

export function buildRouteTimeContext(item) {
  return {
    routeId: getRouteIdFromItem(item),
    originLocation: item?.depature_from ?? "",
    originTime: item?.depature_time ?? "",
    destinationLocation: item?.arrival_to ?? "",
    destinationTime: item?.arrival_time ?? "",
  };
}

function routeMatchesPickupDrop(stopsRaw, pickUp, dropping) {
  return routeContainsPickupBeforeDrop(stopsRaw, pickUp, dropping);
}

/** Normalize `available_dates` to sorted unique YYYY-MM-DD strings. */
export function normalizeAvailableDates(val) {
  const arr = parseJsonField(val, []);
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  const out = [];
  for (const item of arr) {
    const d = item == null ? "" : String(item).trim().slice(0, 10);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(d) || seen.has(d)) continue;
    seen.add(d);
    out.push(d);
  }
  return out.sort();
}

/** True when the bus should appear in search results for `formattedDate` (yyyy-MM-dd). */
export function isBusOperatingOnDate(bus, formattedDate) {
  if (!formattedDate) return true;
  if (bus?.runs_everyday !== false) return true;
  return normalizeAvailableDates(bus?.available_dates).includes(formattedDate);
}

export function formatBusScheduleLabel(bus) {
  if (bus?.runs_everyday !== false) return "Daily Service";
  const dates = normalizeAvailableDates(bus?.available_dates);
  if (!dates.length) return "Inactive";
  if (dates.length <= 2) return dates.join(", ");
  return `${dates.length} selected dates`;
}

export async function updateBusAvailability(directusId, { runs_everyday, available_dates }) {
  assertConfigured();
  const payload = {
    runs_everyday: Boolean(runs_everyday),
    available_dates: runs_everyday
      ? []
      : normalizeAvailableDates(available_dates),
  };
  await directusApi.patch(`/items/buses/${directusId}`, payload);
  clearCache("allBuses");
  clearLocalCache(LOCAL_CACHE_KEY_ALL_BUSES);
  return payload;
}

async function fetchBookingsForBusesOnDate(busPrimaryKeys, formattedDate) {
  if (!busPrimaryKeys.length) return new Map();
  const { data } = await directusApi.get("/items/bookings", {
    params: {
      filter: JSON.stringify({
        _and: [
          { date: { _eq: formattedDate } },
          { bus: { _in: busPrimaryKeys } },
        ],
      }),
      limit: -1,
      fields: "*,bus.id,bus.bus_number",
    },
  });
  const rows = data.data || [];
  const map = new Map();
  for (const row of rows) {
    const bid = typeof row.bus === "object" ? row.bus?.id : row.bus;
    if (!bid) continue;
    if (!map.has(bid)) map.set(bid, []);
    map.get(bid).push(row);
  }
  return map;
}

async function getBusPkByNumber(busNumber) {
  const { data } = await directusApi.get("/items/buses", {
    params: {
      filter: JSON.stringify({ bus_number: { _eq: busNumber } }),
      limit: 1,
      fields: "id",
    },
  });
  return data.data?.[0]?.id ?? null;
}

export const fetchBusDetails = (busNumber = "ND4564") => {
  return new Promise(async (resolve, reject) => {
    try {
      assertConfigured();
      if (!getStoredAccessToken()) {
        resolve(null);
        return;
      }
      const { data } = await directusApi.get("/items/buses", {
        params: {
          filter: JSON.stringify({ bus_number: { _eq: busNumber } }),
          limit: 1,
          fields: BUS_FIELDS,
        },
      });
      const raw = data.data?.[0];
      const normalized = raw ? normalizeBus(raw) : null;
      const hydrated = normalized
        ? (await hydrateSeatStructures([normalized]))[0]
        : null;
      resolve(hydrated);
    } catch (error) {
      reject(error);
    }
  });
};

export const fetchAllBuses = async () => {
  assertConfigured();
  const cached = readCache("allBuses");
  if (cached) {
    return hydrateSeatStructures(cached);
  }

  const localCached = readLocalCache(
    LOCAL_CACHE_KEY_ALL_BUSES,
    LOCAL_CACHE_TTL_MS
  );
  if (localCached) {
    const hydrated = await hydrateSeatStructures(localCached);
    writeCache("allBuses", hydrated);
    return hydrated;
  }

  const { data } = await directusApi.get("/items/buses", {
    params: {
      filter: JSON.stringify({ status: { _eq: true } }),
      fields: BUS_FIELDS,
      limit: -1,
    },
  });
  const allBuses = await hydrateSeatStructures((data.data || []).map(normalizeBus));
  writeCache("allBuses", allBuses);
  writeLocalCache(LOCAL_CACHE_KEY_ALL_BUSES, allBuses);
  return allBuses;
};

export const searchBusRoute1 = async (pickUp, dropping, formattedDate) => {
  try {
    assertConfigured();
    const { data } = await directusApi.get("/items/routes", {
      params: {
        filter: JSON.stringify({ status: { _eq: true } }),
        limit: -1,
      },
    });
    const routeDocs = data.data || [];
    const filteredData = routeDocs.filter((obj) =>
      routeMatchesPickupDrop(obj.stops, pickUp, dropping),
    );

    const searchedRouteIds = filteredData.map((r) => r.id);
    if (!searchedRouteIds.length) return [];

    const { data: busRes } = await directusApi.get("/items/buses", {
      params: {
        filter: JSON.stringify({
          _and: [
            { status: { _eq: true } },
            { route: { _in: searchedRouteIds } },
          ],
        }),
        fields: BUS_FIELDS,
        limit: -1,
      },
    });
    const buses = await hydrateSeatStructures((busRes.data || []).map(normalizeBus));
    return buses;
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const getLocationList = async () => {
  try {
    assertConfigured();
    const { data } = await directusApi.get("/items/locations", {
      params: {
        sort: "sort,name",
        limit: -1,
      },
    });
    const rows = data.data || [];
    const out = {};
    rows.forEach((row) => {
      if (row.active === false) return;
      if (row.name) out[row.name] = true;
    });
    return out;
  } catch (error) {
    console.error("getLocationList:", error.message);
    return {};
  }
};

const formatBookings = (bookings) => {
  const reservedSeatsByMale = [];
  const reservedSeatsByFemale = [];

  bookings.forEach((booking) => {
    if (booking.seats) {
      const first = Array.isArray(booking.seats)
        ? booking.seats[0]
        : booking.seats;
      if (first && typeof first === "object") {
        Object.entries(first).forEach(([seatNumber, gender]) => {
          if (typeof gender !== "string") return;
          const g = gender.toLowerCase();
          if (g === "male") reservedSeatsByMale.push(parseInt(seatNumber, 10));
          else if (g === "female")
            reservedSeatsByFemale.push(parseInt(seatNumber, 10));
        });
      }
    }
  });

  return { reservedSeatsByMale, reservedSeatsByFemale };
};

/** Used for seat map + collision checks: these bookings must not block seats. */
function bookingBlocksSeatsForMap(booking) {
  const s = booking?.status;
  if (s === "hold") return false;
  if (s === "failed") return false;
  if (s === "cancelled" || s === "canceled") return false;
  return true;
}

export const searchBusRoute = async (pickUp, dropping, formattedDate) => {
  try {
    assertConfigured();
    const { data } = await directusApi.get("/items/routes", {
      params: {
        filter: JSON.stringify({ status: { _eq: true } }),
        limit: -1,
      },
    });
    const routeDocs = data.data || [];
    const filteredData = routeDocs.filter((obj) =>
      routeMatchesPickupDrop(obj.stops, pickUp, dropping),
    );

    const searchedRouteIds = filteredData.map((r) => r.id);
    if (!searchedRouteIds.length) return [];

    const { data: busRes } = await directusApi.get("/items/buses", {
      params: {
        filter: JSON.stringify({
          _and: [
            { status: { _eq: true } },
            { route: { _in: searchedRouteIds } },
          ],
        }),
        fields: BUS_FIELDS,
        limit: -1,
      },
    });
    const buses = (await hydrateSeatStructures((busRes.data || []).map(normalizeBus))).filter(
      (bus) => isBusOperatingOnDate(bus, formattedDate)
    );
    const busPks = buses.map((b) => b.directus_id).filter(Boolean);
    const bookingsByBus = await fetchBookingsForBusesOnDate(
      busPks,
      formattedDate
    );

    return buses.map((bus) => {
      const list = bookingsByBus.get(bus.directus_id) || [];
      const bookingsData = list.filter(bookingBlocksSeatsForMap);
      let reservedSeatsByMale = [];
      let reservedSeatsByFemale = [];
      if (bookingsData.length > 0) {
        const formatted = formatBookings(bookingsData);
        reservedSeatsByMale = formatted.reservedSeatsByMale;
        reservedSeatsByFemale = formatted.reservedSeatsByFemale;
      }
      return {
        ...bus,
        structure: bus.structure,
        seat_map: bus.seat_map ?? bus.structure?.seat_map ?? null,
        routeSet: bus.route,
        bookings: bookingsData,
        reservedSeatsByMale,
        reservedSeatsByFemale,
      };
    });
  } catch (error) {
    console.error(error);
    return [];
  }
};

export const checkAlreadyBooked = async (
  pickup,
  drop,
  formattedDate,
  seats,
  busNumber
) => {
  if (!busNumber) {
    return "Bus number must be specified.";
  }

  let busRouteData;
  try {
    busRouteData = await searchBusRoute(pickup, drop, formattedDate);
  } catch (err) {
    console.error("checkAlreadyBooked searchBusRoute failed:", err);
    return "Unable to verify seat availability. Please try again.";
  }

  if (!busRouteData?.length) {
    return "No buses found for this route/date. Please search again.";
  }

  const bus = busRouteData.find(
    (b) => String(b.bus_number ?? b.id) === String(busNumber)
  );
  if (!bus) {
    return "Selected bus is not available for this route. Please search again.";
  }

  const seatArray = seats.flatMap((seat) => Object.keys(seat).map(Number));
  const reservedSeatsByMale = bus.reservedSeatsByMale || [];
  const reservedSeatsByFemale = bus.reservedSeatsByFemale || [];

  const alreadyReservedSeats = seatArray.filter(
    (seat) =>
      reservedSeatsByMale.includes(seat) || reservedSeatsByFemale.includes(seat)
  );

  if (alreadyReservedSeats.length > 0) {
    return `Selected seats are already reserved: ${alreadyReservedSeats.join(", ")}`;
  }

  return "true";
};

function normalizeDirectusFileField(val) {
  if (val == null || val === "") return null;
  if (typeof val === "object" && val !== null && val.id) return val.id;
  return val;
}

export const saveBooking = async (bookingDetails) => {
  const allSeatsAvailable = await checkAlreadyBooked(
    bookingDetails.from,
    bookingDetails.to,
    bookingDetails.date,
    bookingDetails.seats,
    bookingDetails.bus_number
  );

  if (allSeatsAvailable !== "true") {
    return {
      success: false,
      message: allSeatsAvailable,
    };
  }

  try {
    assertConfigured();
    const { bus_number } = bookingDetails;
    if (!bus_number) {
      throw new Error("Bus number must be specified in the booking details.");
    }
    const busPk = await getBusPkByNumber(bus_number);
    if (!busPk) {
      throw new Error("Bus not found.");
    }

    const payload = {
      bus: busPk,
      date: bookingDetails.date,
      from: bookingDetails.from,
      to: bookingDetails.to,
      passenger_name: bookingDetails.passenger_name,
      mobile_no: bookingDetails.mobile_no,
      email: bookingDetails.email || null,
      nic: bookingDetails.nic || null,
      payment_method: bookingDetails.payment_method || null,
      payment_slip: normalizeDirectusFileField(bookingDetails.payment_slip),
      slip: normalizeDirectusFileField(bookingDetails.slip),
      fee: bookingDetails.fee,
      seats: bookingDetails.seats,
      status: bookingDetails.status || "booked",
      ...(bookingDetails.payment_status !== undefined
        ? { payment_status: bookingDetails.payment_status }
        : bookingDetails.status === "pending_payment"
          ? { payment_status: "pending" }
          : {}),
      ...(bookingDetails.payment_reference
        ? { payment_reference: bookingDetails.payment_reference }
        : {}),
      ...(bookingDetails.payment_outcome_detail
        ? { payment_outcome_detail: bookingDetails.payment_outcome_detail }
        : {}),
    };

    const { data } = await directusApi.post("/items/bookings", payload);
    clearUniqueBookingsByNicCaches();
    clearCache("totalBookingsToday");
    return {
      success: true,
      message: "Booking successfully saved",
      bookingId: data?.data?.id,
    };
  } catch (error) {
    return {
      success: false,
      message: error.response?.data?.errors?.[0]?.message || error.message,
    };
  }
};

/**
 * @param {string} bus_number
 * @param {string} date - yyyy-MM-dd
 * @param {{ status?: string }} [opts] - e.g. `{ status: "booked" }` to restrict by status
 */
export const fetchBooking = async (bus_number, date, opts = {}) => {
  assertConfigured();
  const busPk = await getBusPkByNumber(bus_number);
  if (!busPk) return [];

  const and = [{ bus: { _eq: busPk } }, { date: { _eq: date } }];
  if (opts.status != null && String(opts.status).trim() !== "") {
    and.push({ status: { _eq: String(opts.status).trim() } });
  }

  const { data } = await directusApi.get("/items/bookings", {
    params: {
      filter: JSON.stringify({
        _and: and,
      }),
      limit: -1,
      fields: "*",
    },
  });

  return (data.data || []).map((row) => ({
    ...row,
    id: row.id,
    bus_number,
    payment_slip: normalizeDirectusFileField(row.payment_slip),
    slip: normalizeDirectusFileField(row.slip),
  }));
};

export const updateBookings = async (booking) => {
  assertConfigured();
  const patch = {};
  if (booking.status !== undefined) patch.status = booking.status;
  if (booking.payment_method !== undefined)
    patch.payment_method = booking.payment_method;
  if (booking.payment_slip !== undefined)
    patch.payment_slip = normalizeDirectusFileField(booking.payment_slip);
  if (booking.slip !== undefined)
    patch.slip = normalizeDirectusFileField(booking.slip);
  if (booking.payment_status !== undefined)
    patch.payment_status = booking.payment_status;
  if (booking.payment_outcome_detail !== undefined)
    patch.payment_outcome_detail = booking.payment_outcome_detail;
  if (booking.payment_reference !== undefined)
    patch.payment_reference = booking.payment_reference;
  if (booking.callback_status !== undefined)
    patch.callback_status = booking.callback_status;

  await directusApi.patch(`/items/bookings/${booking.id}`, patch);
  clearUniqueBookingsByNicCaches();
  clearCache("totalBookingsToday");
  return "success";
};

/**
 * Best-effort mark when the user closes the tab mid-payment (browser may still deliver after unload).
 * Hits Cybersource extension so payment_status becomes `abandoned` (not only status=cancelled).
 */
export function cancelBookingHoldKeepalive(bookingId) {
  if (!bookingId) return;
  const paymentApi =
    (typeof process !== "undefined" &&
      process.env.REACT_APP_PAYMENT_API_URL?.trim()) ||
    directusBaseUrl ||
    "";
  if (paymentApi) {
    try {
      fetch(
        `${paymentApi.replace(/\/+$/, "")}/directus-extension-endpoint-cybersource/mark-payment-lifecycle`,
        {
          method: "POST",
          keepalive: true,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            bookingId,
            paymentStatus: "abandoned",
            detail: "pagehide",
            releaseSeats: true,
          }),
        },
      );
      return;
    } catch {
      // fall through
    }
  }
  if (!directusBaseUrl) return;
  const session = getStoredAccessToken()?.trim();
  const staticTok =
    (typeof process !== "undefined" &&
      process.env.REACT_APP_DIRECTUS_FILE_UPLOAD_TOKEN?.trim()) ||
    "";
  const token = session || staticTok;
  if (!token) return;
  const url = `${directusBaseUrl}/items/bookings/${encodeURIComponent(bookingId)}`;
  try {
    fetch(url, {
      method: "PATCH",
      keepalive: true,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        status: "cancelled",
        payment_status: "abandoned",
        payment_outcome_detail: "pagehide",
      }),
    });
  } catch {
    // ignore
  }
}

/**
 * Aggregate bookings by NIC when set, otherwise by mobile (many online bookings have no NIC).
 * @param {{ status?: string, bustCache?: boolean }} [opts] - Pass `{ status: "booked" }` to count only confirmed bookings. Use `bustCache: true` to skip memory/localStorage (e.g. Customers admin view).
 */
export const getUniqueBookingsByNIC = async (opts = {}) => {
  const status =
    opts.status != null && String(opts.status).trim() !== ""
      ? String(opts.status).trim()
      : null;
  const memKey = status
    ? `uniqueBookingsByNIC__${status}${UNIQUE_BOOKINGS_CACHE_TAG}`
    : "uniqueBookingsByNIC";
  const localKey = status
    ? `${LOCAL_CACHE_KEY_UNIQUE_BOOKINGS}__${status}${UNIQUE_BOOKINGS_CACHE_TAG}`
    : LOCAL_CACHE_KEY_UNIQUE_BOOKINGS;

  if (opts.bustCache) {
    clearCache(memKey);
    clearLocalCache(localKey);
  }

  const cached = readCache(memKey);
  if (cached && !opts.bustCache) return cached;

  const localCached = readLocalCache(localKey, LOCAL_CACHE_TTL_MS);
  if (localCached && !opts.bustCache) {
    writeCache(memKey, localCached);
    return localCached;
  }

  assertConfigured();
  const hasContact = {
    _or: [{ nic: { _nnull: true } }, { mobile_no: { _nnull: true } }],
  };
  const and = [hasContact];
  if (status) {
    and.push({ status: { _eq: status } });
  }
  const { data } = await directusApi.get("/items/bookings", {
    params: {
      filter: JSON.stringify({ _and: and }),
      limit: -1,
      // `*` so we read whatever NIC-like field exists; role must have read access on those columns.
      fields: "*",
    },
  });

  const uniqueBookings = {};
  (data.data || []).forEach((bookingData) => {
    const nic = pickNicFromBooking(bookingData);
    const mobile =
      bookingData.mobile_no != null && String(bookingData.mobile_no).trim() !== ""
        ? String(bookingData.mobile_no).trim()
        : "";
    if (!nic && !mobile) return;

    const dedupeKey = nic ? `nic:${nic}` : `mob:${mobile}`;
    if (uniqueBookings[dedupeKey]) {
      const row = uniqueBookings[dedupeKey];
      row.bookings++;
      if (nic && (!row.nic || row.nic === "—")) {
        row.nic = nic;
      }
      const name = (bookingData.passenger_name || "").trim();
      if (name && (!row.name || row.name === "—")) {
        row.name = name;
      }
    } else {
      uniqueBookings[dedupeKey] = {
        name: (bookingData.passenger_name || "").trim() || "—",
        bookings: 1,
        nic: nic || "—",
        mobile_no: mobile || "—",
      };
    }
  });

  const result = Object.values(uniqueBookings);
  writeCache(memKey, result);
  writeLocalCache(localKey, result);
  return result;
};

export const getTotalBookingsForToday = async () => {
  const cached = readCache("totalBookingsToday");
  if (cached !== null) return cached;

  const today = new Date().toISOString().slice(0, 10);
  assertConfigured();

  const response = await directusApi.get("/items/bookings", {
    params: {
      filter: JSON.stringify({ date: { _eq: today } }),
      limit: 0,
      meta: "filter_count",
    },
  });

  const body = response?.data;
  const totalBookings =
    body?.meta?.filter_count ??
    (Array.isArray(body?.data) ? body.data.length : 0);
  writeCache("totalBookingsToday", totalBookings);
  return totalBookings;
};

/**
 * Count prior confirmed bookings with exact mobile_no match (status = booked).
 * Public booking role needs read on bookings (or use static token with that permission).
 * @param {string} mobileNo
 * @returns {Promise<number>}
 */
export const countBookedByExactMobile = async (mobileNo) => {
  assertConfigured();
  const mobile = String(mobileNo || "").trim();
  if (!mobile) return 0;

  const response = await directusApi.get("/items/bookings", {
    params: {
      filter: JSON.stringify({
        _and: [
          { status: { _eq: "booked" } },
          { mobile_no: { _eq: mobile } },
        ],
      }),
      limit: 0,
      meta: "filter_count",
    },
  });

  // Axios: response.data = Directus body { meta, data }. meta is NOT a top-level axios field.
  const body = response?.data;
  const n =
    body?.meta?.filter_count ??
    (Array.isArray(body?.data) ? body.data.length : 0);
  return Number.isFinite(Number(n)) ? Number(n) : 0;
};
